import React, {
  useMemo,
  useRef,
  useState,
  KeyboardEvent,
  RefObject,
} from 'react';
import {
  useForm,
} from 'react-hook-form';
import Controller from 'core/components/FormController';
import Editable from 'core/components/Editable';
import BaseDialog from 'pages/Dashboard/components/Dialog';
import useUpdateMediaHandler from 'pages/Dashboard/hooks/useUpdateMediaHandler';
import {
  MultiMediaResponse,
} from 'dtos';
import MediaPreview from 'pages/Dashboard/components/MediaPreview';
import {
  useNotify,
} from 'core/hooks';
import IconButton from '@mui/material/IconButton';
import MaximizeIcon from 'core/icons/maximize';
import MinimizeIcon from 'core/icons/minimize';
import {
  mediaEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import clsx from 'clsx';
import Input from 'pages/Dashboard/components/Input';
import {
  getFileParts,
  notEmptyString,
} from '@populateui/utils';
import {
  useGetMediaTypesList,
} from 'pages/Dashboard/pages/Charts/pages/Details/components/hooks';
import {
  useGetMediaSettings,
} from 'pages/Dashboard/hooks/useUploadPatientMedia';
import MediaTypeMenu from 'pages/Dashboard/pages/Charts/pages/Details/components/Multimedia/MediaTypeMenu';
import Button from 'pages/Dashboard/components/Button';
import EditIcon from 'core/icons/edit';
import ToolTip from 'core/components/Tooltip';

type EditDialogProps = MultiMediaResponse & {
  onClose: () => void;
};

export default function EditDialog({
  onClose,
  ...props
}: EditDialogProps) {
  const notify = useNotify();
  const updateMedia = useUpdateMediaHandler();
  const {
    handleSubmit,
    control,
    reset,
    formState: { isSubmitting, isDirty },
  } = useForm<MultiMediaResponse>({
    mode: 'all',
    defaultValues: {
      ...props,
    },
  });

  const onSubmit = async ({ mediaId, ...data }: MultiMediaResponse, fieldName: string) => {
    await updateMedia({
      patientMultiMediaId: mediaId,
      ...data,
    });

    mediaEventChannel.emit('onMediaUpdated');

    notify({
      variant: 'success',
      message: `${fieldName} updated successfully`,
    });

    reset(data, {
      keepValues: true,
      keepDirty: false,
    });
  };

  const [maximized, setMaximized] = useState(false);

  const FullScreenIcon = useMemo(() => (
    maximized ? MinimizeIcon : MaximizeIcon
  ), [maximized]);

  const mediaTypes = useGetMediaTypesList();

  const readOnly = !mediaTypes.some((mediaType) => mediaType.value === props.multimediaType);

  const { multimediaType } = props;

  const [mediaType, setMediaType] = useState(multimediaType);

  const { mediaTypeDisplayNameLookUp = {} } = useGetMediaSettings();
  const mediaTypeDisplayName = useMemo(
    () => mediaTypeDisplayNameLookUp?.[mediaType!] ?? mediaType,
    [mediaTypeDisplayNameLookUp, mediaType],
  );

  const mediaTypeInputRef = useRef<HTMLInputElement>(null!);
  const fileNameInputRef = useRef<HTMLInputElement>(null!);

  const onFileNameUpdate = (data: MultiMediaResponse) => {
    if (isDirty) {
      onSubmit(data, 'File name');
    }
  };

  const onMediaTypeUpdate = (data: MultiMediaResponse) => {
    onSubmit(data, 'Media category');
    mediaTypeInputRef.current?.blur();
    setMediaType(data.multimediaType);
  };

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  return (
    <BaseDialog
      autoFocusOptions={{ enabled: false }}
      title={(
        <div className="w-full flex flex-col gap-1 !h-16">
          <Controller
            name="fileName"
            control={control}
            render={({ field: { value, onChange, ...field } }) => {
              const fileName = value ?? '';
              const [namePart, extension] = getFileParts(fileName);
              return (
                <Editable
                  readonly={maximized || readOnly}
                  idleView={(
                    <h1 className="font-semibold text-xl w-fit break-all">
                      {fileName}
                    </h1>
                  )}
                  showActions={false}
                  isLoading={isSubmitting}
                  controlRef={fileNameInputRef as RefObject<HTMLElement>}
                  onSubmit={handleSubmit(onFileNameUpdate)}
                  tooltip="Update File Name"
                >
                  {(onSubmit, onCancel) => (
                    <Input
                      {...field}
                      value={namePart}
                      id="file-name"
                      className="w-full bg-white min-w-[25rem] !h-9"
                      inputRef={fileNameInputRef}
                      showErrorText={false}
                      onBlur={onSubmit}
                      onChange={(updatedVal) => {
                        onChange([updatedVal, extension].join('.'));
                      }}
                      onKeyDown={(e: KeyboardEvent<HTMLElement>) => {
                        e.stopPropagation();
                        if (e.key === 'Escape') {
                          onCancel();
                        } else if (e.key === 'Enter') {
                          onSubmit();
                        }
                      }}
                    />
                  )}
                </Editable>
              );
            }}
          />
          {!maximized && (
            <Controller
              name="multimediaType"
              control={control}
              render={({ field: { value, onChange, ...field } }) => (readOnly ? (
                <p className="font-semibold text-xs">{mediaTypeDisplayName}</p>
              ) : (
                <>
                  <ToolTip title="Edit media type">
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        setAnchorEl(e.currentTarget);
                      }}
                      variant="text"
                      className="!w-1/4 flex items-center gap-1 !px-1 !py-1"
                    >
                      <span className="text-sm">{mediaTypeDisplayName}</span>
                      <EditIcon className="!w-5 !h-5 !text-gray-600 !mt-1" />
                    </Button>
                  </ToolTip>
                  <MediaTypeMenu
                    value={value ?? ''}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'left',
                    }}
                    onChange={(newValue) => {
                      if (notEmptyString(newValue.mediaType)) {
                        onChange(newValue.mediaType);
                        handleSubmit(onMediaTypeUpdate)();
                      }
                    }}
                    anchorEl={anchorEl}
                    onClose={() => { setAnchorEl(null); }}
                  />
                </>
              ))}
            />
          )}
        </div>
      )}
      classes={{
        root: clsx(
          '!flex !h-[calc(100vh-24px)] !max-h-[calc(100vh-24px)] !w-full',
          'flex-col overflow-hidden !p-0 !space-y-0',
        ),
        dialog: clsx('hidden-backdrop !m-0', { 'populate-iframe-modal': !maximized, 'populate-dialog': maximized }),
        bodyRoot: '!w-screen !m-0 !max-w-[calc(100%-12px)] !overflow-hidden',
        container: '!justify-start',
        title: 'shrink-0 border-b border-b-gray-300 p-4',
      }}
      maxWidth={false}
      fullWidth
      onClose={onClose}
      headerActions={(
        <IconButton
          className="relative -top-3 -right-3 !w-8 !h-8"
          onClick={() => { setMaximized(!maximized); }}
        >
          <FullScreenIcon className="!w-4 !h-4" />
        </IconButton>
      )}
      open
    >
      <MediaPreview
        media={props}
        isFullScreen={maximized}
        className="min-h-0 !flex-1 !h-auto"
      />
    </BaseDialog>
  );
}
